import { FieldArray, Form, Formik } from "formik";
import InputField from "../../ui/form/InputField";
import { NomineeValidation } from "../../../validation/formValidation";
import usePost from "../../../hooks/api/usePost";
import apiRequest from "../../../utils/apiRequest";
import FileInputField from "../../ui/form/FileInputField";
import Button from "../../ui/Button";
import React, { useState } from "react";
import { formatDateForInput } from "../../../utils/helper";
import { FaTrash } from "react-icons/fa";
import { MdAddCircleOutline } from "react-icons/md";
import axios from "axios";
import toast from "react-hot-toast";
import authManager from "../../../utils/authManager";
import handleApiError from "./handleApiError";
import FullScreenLoader from "../../ui/FullScreenLoader";
import PaymentOptions from "./PaymentOptions";
import { DASHBOARD_ROUTE, HOME_ROUTE } from "../../../routes/web/rootRoutes";
import { useNavigate } from "react-router-dom";

const NominationForm = ({ data = {} }) => {
  const { post, isLoading: postLoading, serverErrors } = usePost(apiRequest.createForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [submissionResponse, setSubmissionResponse] = useState(null);
  const [submittedFormData, setSubmittedFormData] = useState(null);
       const navigate = useNavigate(); 

  const userString = localStorage.getItem('user');
  let union_code;
  if (userString) {
    const user = JSON.parse(userString);
    union_code = user.union_code;
  }

  const initialValues = {
    // Member Details
    member_no: data?.member_no || union_code || "",
    date: data?.date || formatDateForInput(new Date()),
    member_name: data?.member_name || "",
    designation: data?.designation || "",
    address: data?.address || "",
    mobile: data?.mobile || "",
    email: data?.email || "",
    native_address: data?.native_address || "",
    dob: data?.dob ? formatDateForInput(data.dob) : "",

    // Family Details
    family_details: data?.family_details?.length
      ? data.family_details
      : [{ name: "", relation: "", dob: "", addhar_no: "" }],

    // Nominee Details
    nominee_name: data?.nominee_name || "",
    nominee_age: data?.nominee_age || "",
    nominee_dob: data?.nominee_dob || "",
    nominee_relationship: data?.nominee_relationship || "",
    nominee_address: data?.nominee_address || "",
    nominee_mobile: data?.nominee_mobile || "",
    nominee_email: data?.nominee_email || "",

    // File Uploads
    member_applicant_signature: null,
    member_member_photo: null,
    member_nominee_photo: null,

    // Agreement
    agree: false,
  };

  const handleSubmit = async (values, { setErrors, resetForm }) => {
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();

      // Handle family_details separately
      Object.entries(values).forEach(([key, value]) => {
        if (key === "family_details") {
          value.forEach((member, i) => {
            formData.append(`family_details[${i}][name]`, member.name);
            formData.append(`family_details[${i}][relation]`, member.relation);
            formData.append(`family_details[${i}][dob]`, member.dob);
            formData.append(`family_details[${i}][addhar_no]`, member.addhar_no);
          });
        } else if (key !== "agree" && value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      // Debug log
      console.log("Submitting Nomination Form:");
      for (let [key, val] of formData.entries()) {
        console.log(`${key}:`, val instanceof File ? `File: ${val.name}` : val);
      }

      const token = authManager.getAccessToken();
      if (!token) {
        toast.error("Authentication token not found. Please login again.");
        setIsSubmitting(false);
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        toast.error("API URL not configured");
        setIsSubmitting(false);
        return;
      }

      const response = await axios.post(
        `${apiUrl}nomination`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          timeout: 30000,
        }
      );

      console.log("✅ Nomination form submitted:", response.data);
      
      setSubmissionResponse(response.data);
      setSubmittedFormData(values);
      
      toast.success("Nomination form submitted successfully!");
      resetForm();

      setTimeout(() => {
            navigate(DASHBOARD_ROUTE);

        setIsSubmitting(false);
        setShowPayment(true);
      }, 1500);
      
    } catch (error) {
      console.error("Submission error:", error);
      
      if (error.response) {
        console.error("Server Error:", error.response.data);
        toast.error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
      } else if (error.request) {
        console.error("No response from server");
        toast.error("No response from server. Please check your internet connection.");
      } else {
        console.error("Request error:", error.message);
        toast.error(`Error: ${error.message}`);
      }
      
      handleApiError(error, setErrors, "Failed to submit nomination form");
      setIsSubmitting(false);
    }
  };

  const handleClosePayment = () => {
    setShowPayment(false);
    window.location.href = HOME_ROUTE;
  };

  if (showPayment) {
    return (
      <>
        {showPayment && (
          <PaymentOptions
            onClose={handleClosePayment}
          />
        )}
      </>
    );
  }

  return (
    <>
      {isSubmitting && (
        <FullScreenLoader message="Submitting nomination application..." />
      )}

      <div className="max-w-7xl mx-auto p-6 bg-gradient-to-br min-h-screen">
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <div className="bg-gradient-to-r from-violet-500 to-violet-900 text-white p-6">
            <h1 className="text-center text-3xl font-bold mb-2">
              NOMINATION FORM
            </h1>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={NomineeValidation}
            onSubmit={handleSubmit}
          >
            {(formik) => {
              const setFieldValue = (field, value) => {
                formik.setFieldValue(field, value);
              };

              return (
                <Form className="p-8">
                  {/* Section 1: Member Number & Date */}
                  <div className="mb-8 p-6 bg-gray-50 rounded-lg border-l-4 border-violet-500">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Member No. <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="member_no"
                          placeholder="Enter Membership No"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.member_no}
                          error={formik.touched.member_no && formik.errors.member_no}
                          serverError={serverErrors?.member_no}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Date <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="date"
                          type="date"
                          readOnly
                          placeholder="Select Date"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.date}
                          error={formik.touched.date && formik.errors.date}
                          serverError={serverErrors?.date}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Address Section */}
                  <div className="mb-8 p-6 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                    <h3 className="text-xl font-semibold text-gray-800 mb-1">To,</h3>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">The President / General Secretary</h3>
                    <h3 className="text-lg font-medium text-gray-700">Association of Cine & Television Art Directors & Costume Designers</h3>
                  </div>

                  {/* Section 3: Member Personal Details */}
                  <div className="mb-8 p-6 bg-green-50 rounded-lg border-l-4 border-green-500">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="mr-2">👤</span> Member Personal Details
                    </h2>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Member's Name <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="member_name"
                          placeholder="Enter Member Name"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.member_name}
                          error={formik.touched.member_name && formik.errors.member_name}
                          serverError={serverErrors?.member_name}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Designation <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="designation"
                          placeholder="Enter designation"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.designation}
                          error={formik.touched.designation && formik.errors.designation}
                          serverError={serverErrors?.designation}
                        />
                      </div>

                      <div className="space-y-2 lg:col-span-2">
                        <label className="block font-semibold text-gray-700">
                          Residential Address <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="address"
                          placeholder="Enter complete address"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.address}
                          error={formik.touched.address && formik.errors.address}
                          serverError={serverErrors?.address}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Mobile No. <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="mobile"
                          placeholder="Enter mobile number"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.mobile}
                          error={formik.touched.mobile && formik.errors.mobile}
                          serverError={serverErrors?.mobile}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Email ID <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="email"
                          type="email"
                          placeholder="Enter email address"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.email}
                          error={formik.touched.email && formik.errors.email}
                          serverError={serverErrors?.email}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Date of Birth <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="dob"
                          type="date"
                          placeholder="Enter your dob"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.dob}
                          error={formik.touched.dob && formik.errors.dob}
                          serverError={serverErrors?.dob}
                        />
                      </div>

                      <div className="space-y-2 lg:col-span-2">
                        <label className="block font-semibold text-gray-700">
                          Native Place Address <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="native_address"
                          placeholder="Enter Native Place Address"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.native_address}
                          error={formik.touched.native_address && formik.errors.native_address}
                          serverError={serverErrors?.native_address}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Family Details */}
                  <div className="mb-8 p-6 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="mr-2">👨‍👩‍👧‍👦</span> Family Member Details
                    </h2>

                    <FieldArray name="family_details">
                      {({ push, remove }) => (
                        <>
                          <div className="overflow-x-auto">
                            <table className="w-full text-left border border-collapse border-gray-300">
                              <thead>
                                <tr className="bg-gray-100">
                                  <th className="p-2 text-sm font-semibold border border-gray-300">Name</th>
                                  <th className="p-2 text-sm font-semibold border border-gray-300">Relation</th>
                                  <th className="p-2 text-sm font-semibold border border-gray-300">Date of Birth</th>
                                  <th className="p-2 text-sm font-semibold border border-gray-300">Aadhar No</th>
                                  <th className="p-2 text-sm font-semibold text-center border border-gray-300">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {formik.values.family_details.map((_, index) => (
                                  <tr key={index} className="hover:bg-gray-50">
                                    <td className="p-2 border border-gray-300">
                                      <InputField
                                        id={`family_details.${index}.name`}
                                        type="text"
                                        placeholder="Enter Name"
                                        value={formik.values.family_details[index].name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                          formik.touched.family_details?.[index]?.name &&
                                          formik.errors.family_details?.[index]?.name
                                        }
                                      />
                                    </td>
                                    <td className="p-2 border border-gray-300">
                                      <InputField
                                        id={`family_details.${index}.relation`}
                                        type="text"
                                        placeholder="Enter relation"
                                        value={formik.values.family_details[index].relation}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                          formik.touched.family_details?.[index]?.relation &&
                                          formik.errors.family_details?.[index]?.relation
                                        }
                                      />
                                    </td>
                                    <td className="p-2 border border-gray-300">
                                      <InputField
                                        id={`family_details.${index}.dob`}
                                        type="date"
                                        placeholder="Enter dob"
                                        value={formik.values.family_details[index].dob}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                          formik.touched.family_details?.[index]?.dob &&
                                          formik.errors.family_details?.[index]?.dob
                                        }
                                      />
                                    </td>
                                    <td className="p-2 border border-gray-300">
                                      <InputField
                                        id={`family_details.${index}.addhar_no`}
                                        type="text"
                                        placeholder="Enter Aadhar no"
                                        value={formik.values.family_details[index].addhar_no}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                          formik.touched.family_details?.[index]?.addhar_no &&
                                          formik.errors.family_details?.[index]?.addhar_no
                                        }
                                      />
                                    </td>
                                    <td className="p-2 text-center border border-gray-300">
                                      <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="text-red-600 hover:text-red-800 transition-colors"
                                        disabled={formik.values.family_details.length === 1}
                                      >
                                        <FaTrash />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div className="flex items-center justify-end mt-4">
                            <button
                              type="button"
                              onClick={() => push({ name: "", relation: "", dob: "", addhar_no: "" })}
                              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
                            >
                              <MdAddCircleOutline /> Add Family Member
                            </button>
                          </div>
                        </>
                      )}
                    </FieldArray>
                  </div>

                  {/* Section 5: Nominee Details */}
                  <div className="mb-8 p-6 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="mr-2">👤</span> Nominee Details
                    </h2>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Nominee Name <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="nominee_name"
                          placeholder="Enter nominee name"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.nominee_name}
                          error={formik.touched.nominee_name && formik.errors.nominee_name}
                          serverError={serverErrors?.nominee_name}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Nominee Age <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="nominee_age"
                          type="number"
                          placeholder="Enter nominee age"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.nominee_age}
                          error={formik.touched.nominee_age && formik.errors.nominee_age}
                          serverError={serverErrors?.nominee_age}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Nominee DOB <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="nominee_dob"
                          type="date"
                          placeholder="Enter nominee dob"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.nominee_dob}
                          error={formik.touched.nominee_dob && formik.errors.nominee_dob}
                          serverError={serverErrors?.nominee_dob}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Nominee Relationship <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="nominee_relationship"
                          placeholder="Enter nominee relationship"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.nominee_relationship}
                          error={formik.touched.nominee_relationship && formik.errors.nominee_relationship}
                          serverError={serverErrors?.nominee_relationship}
                        />
                      </div>

                      <div className="space-y-2 lg:col-span-2">
                        <label className="block font-semibold text-gray-700">
                          Nominee Address <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="nominee_address"
                          placeholder="Enter nominee address"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.nominee_address}
                          error={formik.touched.nominee_address && formik.errors.nominee_address}
                          serverError={serverErrors?.nominee_address}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Nominee Mobile No <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="nominee_mobile"
                          placeholder="Enter nominee mobile"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.nominee_mobile}
                          error={formik.touched.nominee_mobile && formik.errors.nominee_mobile}
                          serverError={serverErrors?.nominee_mobile}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                          Nominee Email <span className="text-red-500">*</span>
                        </label>
                        <InputField
                          id="nominee_email"
                          type="email"
                          placeholder="Enter nominee email"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.nominee_email}
                          error={formik.touched.nominee_email && formik.errors.nominee_email}
                          serverError={serverErrors?.nominee_email}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 6: Documents Upload */}
                  <div className="mb-8 p-6 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="mr-2">📎</span> Documents Upload
                      <span className="text-sm font-normal text-gray-600 ml-2">(Please upload required documents)</span>
                    </h2>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div className="space-y-2 p-4 bg-blue-50 rounded-lg">
                        <label className="block font-semibold text-gray-700">
                          Applicant Signature <span className="text-red-500">*</span>
                        </label>
                        <FileInputField
                          id="member_applicant_signature"
                          onChange={(event) => {
                            setFieldValue("member_applicant_signature", event.currentTarget.files[0]);
                          }}
                          onBlur={formik.handleBlur}
                          error={formik.touched.member_applicant_signature && formik.errors.member_applicant_signature}
                          serverError={serverErrors?.member_applicant_signature}
                        />
                        <p className="text-xs text-gray-500">Upload scanned signature</p>
                      </div>

                      <div className="space-y-2 p-4 bg-green-50 rounded-lg">
                        <label className="block font-semibold text-gray-700">
                          Member Photo <span className="text-red-500">*</span>
                        </label>
                        <FileInputField
                          id="member_member_photo"
                          onChange={(event) => {
                            setFieldValue("member_member_photo", event.currentTarget.files[0]);
                          }}
                          onBlur={formik.handleBlur}
                          error={formik.touched.member_member_photo && formik.errors.member_member_photo}
                          serverError={serverErrors?.member_member_photo}
                        />
                        <p className="text-xs text-gray-500">Passport size photo</p>
                      </div>

                      <div className="space-y-2 p-4 bg-yellow-50 rounded-lg">
                        <label className="block font-semibold text-gray-700">
                          Nominee Photo <span className="text-red-500">*</span>
                        </label>
                        <FileInputField
                          id="member_nominee_photo"
                          onChange={(event) => {
                            setFieldValue("member_nominee_photo", event.currentTarget.files[0]);
                          }}
                          onBlur={formik.handleBlur}
                          error={formik.touched.member_nominee_photo && formik.errors.member_nominee_photo}
                          serverError={serverErrors?.member_nominee_photo}
                        />
                        <p className="text-xs text-gray-500">Nominee's passport size photo</p>
                      </div>
                    </div>
                  </div>

                  {/* Section 7: Declaration */}
                  <div className="mb-8 p-6 bg-red-50 rounded-lg border-l-4 border-red-500">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="mr-2">📜</span> Declaration
                    </h2>

                    <div className="space-y-4">
                      <p className="text-sm text-gray-700">
                        I hereby declare that the information provided above is true and correct to the best of my knowledge. 
                        I nominate the above-mentioned person as my nominee for all benefits from the association.
                      </p>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="agree"
                          name="agree"
                          onChange={(e) => formik.setFieldValue('agree', e.target.checked)}
                          className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
                        />
                        <label htmlFor="agree" className="ml-2 text-sm text-gray-700">
                          I confirm that all information provided is correct and I understand that any false information may lead to rejection of this nomination <span className="text-red-500">*</span>
                        </label>
                      </div>
                      {formik.touched.agree && formik.errors.agree && (
                        <p className="mt-1 text-xs text-red-500">{formik.errors.agree}</p>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="mt-8 flex justify-end items-center bg-gray-50 p-6 rounded-lg">
                    <Button
                      variant="primary"
                      type="submit"
                      isLoading={postLoading || isSubmitting}
                      disabled={postLoading || isSubmitting}
                      className="px-8 py-3 text-lg"
                    >
                      {isSubmitting ? 'Submitting...' : '📤 Submit Nomination'}
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default NominationForm;